import { ref, watch, type MaybeRefOrGetter, toValue } from 'vue'
import { getResources } from '@/resources/api'
import type { Resource } from '@/resources/resource_types'
import type { ObjectiveLevel } from '../objectives/objective_levels'

export type ResourceUuidsByLevel = Partial<Record<ObjectiveLevel, string[]>>
export type ResourcesByLevel = Partial<Record<ObjectiveLevel, Resource[]>>

function inUuidOrder(resources: Resource[], uuids: string[]): Resource[] {
  return [...resources].sort((first, second) => uuids.indexOf(first.uuid) - uuids.indexOf(second.uuid))
}

// Each quarter loads on its own, as in Vue 2, so one failed request leaves the others' resources.
async function fetchLevelResources([level, uuids]: [string, string[]]): Promise<[string, Resource[]]> {
  try {
    const resources = await getResources(uuids)
    return [level, inUuidOrder(resources, uuids)]
  } catch (error) {
    console.error(`Failed to fetch the ${level} quarter's objective resources:`, error)
    return [level, []]
  }
}

/**
 * Every quarter's curated resources for a page whose objectives sit in tabs. All quarters load
 * together when the page does, as in Vue 2, because every quarter's panel stays in the page.
 */
export function useQuarterlyObjectiveResources(resourceUuidsByLevel: MaybeRefOrGetter<ResourceUuidsByLevel | undefined>) {
  const resourcesByLevel = ref<ResourcesByLevel>({})
  const isFetching = ref(false)
  let latestRequest = 0

  async function fetchAllLevels(uuidsByLevel: ResourceUuidsByLevel | undefined) {
    const thisRequest = ++latestRequest
    const isSuperseded = () => thisRequest !== latestRequest
    resourcesByLevel.value = {}
    const levelsWithResources = Object.entries(uuidsByLevel ?? {}).filter(([, uuids]) => uuids.length > 0)
    if (levelsWithResources.length === 0) {
      isFetching.value = false
      return
    }
    isFetching.value = true
    const fetchedLevels = await Promise.all(levelsWithResources.map(fetchLevelResources))
    if (isSuperseded()) return
    resourcesByLevel.value = Object.fromEntries(fetchedLevels)
    isFetching.value = false
  }

  watch(() => toValue(resourceUuidsByLevel), fetchAllLevels, { immediate: true })

  return { resourcesByLevel, isFetching }
}
